/**
 * 文件作用：模型/任务路由与模板工具
 * - resolveTaskType: 根据 schema / payload / provider 决定任务类型（text/image/video/...）
 * - toModelKey: 根据集中配置解析模型标识（用于任务系统 model 字段）
 * - buildStatusUrlFromTemplate: 使用模板和 requestId 构造 status 查询 URL，并通过 resolveApiEndpoint 规范化
 *
 * 设计原则：
 * - 优先使用 schema.api.taskType，再使用 payload.api.taskType，最后回退到集中配置的默认策略
 * - 不在此处执行网络请求，仅负责路由与 URL 构造
 *
 * 使用中文注释以满足项目注释规范
 */
import { resolveApiEndpoint } from './apiConfig';
import { isRecord } from './typeGuards';
import type { TaskType, AdapterContext, GenerationPayload } from '../adapters/types';
import { findModelConfig } from '../config/models.config';

const extractTaskType = (source: unknown): TaskType | undefined => {
  if (!isRecord(source)) return undefined;
  const api = source.api;
  if (!isRecord(api)) return undefined;
  const candidate = api.taskType;
  if (typeof candidate === 'string') return candidate as TaskType;
  return undefined;
};

const collectIdentifierCandidates = (
  provider: string,
  payload?: GenerationPayload | Record<string, unknown>,
  schema?: Record<string, unknown>
): string[] => {
  const candidates: string[] = [];
  if (provider) candidates.push(provider);
  if (isRecord(schema) && typeof schema.model === 'string') {
    candidates.push(schema.model);
  }
  if (isRecord(payload)) {
    if (typeof (payload as { model?: unknown }).model === 'string') {
      candidates.push((payload as { model?: string }).model as string);
    }
    const meta = (payload as { meta?: unknown }).meta;
    if (isRecord(meta)) {
      if (typeof meta.provider === 'string') candidates.push(meta.provider);
      if (typeof meta.model === 'string') candidates.push(meta.model);
    }
  }
  return candidates;
};

/**
 * 根据 schema / payload / provider 决定任务类型
 * - schema.api.taskType 优先
 * - payload.api.taskType 次之
 * - 否则根据集中模型配置给出的默认任务类型回退
 */
export const resolveTaskType = (
  provider: string,
  payload?: GenerationPayload | Record<string, unknown> | undefined,
  schema?: Record<string, unknown>
): TaskType => {
  const schemaType = extractTaskType(schema);
  if (schemaType) return schemaType;

  const payloadType = extractTaskType(payload);
  if (payloadType) return payloadType;

  const identifiers = collectIdentifierCandidates(provider, payload, schema);
  for (const id of identifiers) {
    const config = findModelConfig(id);
    if (config) {
      return config.defaultTaskType;
    }
  }

  // 默认回退为 text（保持历史行为）
  return 'text';
};

/**
 * 根据集中配置解析模型标识
 * - 若能匹配 models.config.ts 中的条目，则返回其 modelKey（即 schema.model）
 * - 否则返回传入值的去空格版本
 */
export const toModelKey = (providerOrName?: string): string => {
  if (!providerOrName) return '';
  const raw = String(providerOrName).trim();
  if (!raw) return '';
  const config = findModelConfig(raw);
  if (config) {
    return config.modelKey;
  }
  return raw;
};

/**
 * 使用模板构造 statusUrl，并调用 resolveApiEndpoint 以确保使用 user-configured apiBase
 * - template: 例如 '/v1/video/query?id={id}' 或 '/fal-ai/status/{id}'
 * - requestId: 要替换的 id
 * - ctx: 可选上下文（包含 model 等信息）
 *
 * 返回 { raw, resolved } 结构，若解析失败则返回 undefined。
 */
export interface BuildStatusUrlOptions {
  resolve?: boolean;
  /**
   * 默认为 true，为 false 时忽略警告输出。
   */
  silent?: boolean;
}

export interface StatusUrlResolution {
  raw: string;
  resolved?: string;
}

export const buildStatusUrlFromTemplate = (
  template?: string,
  requestId?: string,
  ctx?: AdapterContext,
  options?: BuildStatusUrlOptions
): StatusUrlResolution | undefined => {
  if (!template) {
    if (!options?.silent) {
      console.warn('buildStatusUrlFromTemplate: 缺少模板，无法构造状态查询 URL。');
    }
    return undefined;
  }
  if (!requestId) {
    if (!options?.silent) {
      console.warn('buildStatusUrlFromTemplate: 缺少 requestId，无法使用模板构造状态查询 URL。');
    }
    return undefined;
  }
  try {
    let raw = String(template);
    raw = raw.replace('{id}', encodeURIComponent(requestId));
    raw = raw.replace('{model}', encodeURIComponent(ctx?.model || ''));

    const stillHasPlaceholder = /\{[^}]+\}/.test(raw);
    if (stillHasPlaceholder && !options?.silent) {
      console.warn(`buildStatusUrlFromTemplate: 模板 "${template}" 中存在未替换的占位符，生成结果 "${raw}"。`);
    }

    if (options?.resolve === false) {
      return { raw };
    }

    try {
      const resolved = resolveApiEndpoint(raw);
      return { raw, resolved };
    } catch (resolveError) {
      if (!options?.silent) {
        console.warn('buildStatusUrlFromTemplate: resolveApiEndpoint 解析失败，已返回 raw。', resolveError);
      }
      return { raw };
    }
  } catch (error) {
    if (!options?.silent) {
      console.warn('buildStatusUrlFromTemplate: 模板解析失败。', error);
    }
    return undefined;
  }
};
